import { type MaybePromise, type WithRequired } from '../types.ts'

export type RequestMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface HTTPClient {
  baseURL: string
  headers: Record<string, number | string | undefined>
  interceptors: {
    request: {
      use(handler: (config: RequiredRequestOptions) => MaybePromise<RequiredRequestOptions>): number
      eject(id: number): void
    }
    response: {
      use(handler: (response: Response) => MaybePromise<Response>): number
      eject(id: number): void
    }
    error: {
      use(handler: (error: RequestError) => MaybePromise<RequestError | never>): number
      eject(id: number): void
    }
  }

  get<TResponseData = any>(url: string, options?: RequestOptions): Promise<{ data: TResponseData }>
  patch<TResponseData = any>(url: string, data?: any, options?: RequestOptions): Promise<{ data: TResponseData }>
  patchForm<TResponseData = any>(url: string, data?: any, options?: RequestOptions): Promise<{ data: TResponseData }>
  put<TResponseData = any>(url: string, data?: any, options?: RequestOptions): Promise<{ data: TResponseData }>
  putForm<TResponseData = any>(url: string, data?: any, options?: RequestOptions): Promise<{ data: TResponseData }>
  post<TResponseData = any>(url: string, data?: any, options?: RequestOptions): Promise<{ data: TResponseData }>
  postForm<TResponseData = any>(url: string, data?: any, options?: RequestOptions): Promise<{ data: TResponseData }>
  delete<TResponseData = any>(url: string, options?: RequestOptions): Promise<{ data: TResponseData }>
  request<TResponseData = any>(options?: RequiredRequestOptions): Promise<{ data: TResponseData }>
}

export interface ErrorResponse extends Response {
  data: any
}

export interface RequestOptions {
  /** The request method to be used. */
  method?: RequestMethod

  /** The URL to which the request is sent. */
  url?: string

  /** The params that are appended to the URL using `URLSearchParams`. */
  params?: Record<string, any>

  /**
   * The request headers to be sent.
   * Will be merged with the default headers.
   */
  headers?: Record<string, number | string | undefined>

  /** The request body to be sent. */
  body?: string

  /**
   * The data to be sent as the request body.
   * If the data is an instance of `FormData`, the `Content-Type` header will be removed.
   * Else, it will be serialized as JSON.
   */
  data?: FormData | unknown

  /**
   * A key to identify the request. If a request with the same key is already in progress, it will be aborted.
   * If no key is provided, the request will be aborted based on the method, URL, and the stack trace of the request.
   * If the key is `null` the request will not be aborted.
   */
  key?: null | number | string | (number | string)[]
}

export type RequiredRequestOptions = WithRequired<RequestOptions, 'method' | 'url'>

export interface CreateHTTPClientOptions {
  baseURL?: string
  headers?: Record<string, number | string | undefined>
}

export class RequestError extends Error {
  response: ErrorResponse

  constructor(response: Response, data: any) {
    super(response.statusText)
    // @ts-expect-error - `data` is assigned directly afterwards.
    this.response = response
    this.response.data = data
  }

  static async fromResponse(response: Response) {
    let data = undefined

    if (response.headers.get('Content-Type') === 'application/json') {
      data = await response.json()
    } else {
      data = await response.text()
    }

    return new RequestError(response, data)
  }
}

export const defaultHeaders = {
  Accept: 'application/json, text/plain, */*',
  'Content-Type': 'application/json',
} as const

/**
 * Create a body for the request.
 */
function toBody(options: RequestOptions): string | FormData | undefined {
  if (options.body) {
    return options.body
  }

  if (options.data) {
    if (options.data instanceof FormData) {
      return options.data
    }

    return JSON.stringify(options.data)
  }

  return undefined
}

/**
 * Create a `FormData` instance from the given data.
 */
function toFormData(data: Record<string, string | Blob | (string | Blob)[]>): FormData {
  const formData = new FormData()

  for (const [key, value] of convertValueForFormData('', data)) {
    formData.append(key, value)
  }

  return formData
}

export function* convertValueForFormData(key: string, value: unknown): Generator<[string, string | Blob]> {
  if (value instanceof File) {
    yield [key, value]

    return
  }

  if (Array.isArray(value)) {
    for (const index in value) {
      for (const [convertedKey, convertedValue] of convertValueForFormData(`${key}[${index}]`, value[index])) {
        yield [convertedKey, convertedValue]
      }
    }

    return
  }

  if (typeof value === 'object' && value !== null) {
    for (const [objectKey, objectValue] of Object.entries(value)) {
      const valueKey = key ? `${key}[${objectKey}]` : objectKey
      for (const [convertedKey, convertedValue] of convertValueForFormData(valueKey, objectValue)) {
        yield [convertedKey, convertedValue]
      }
    }

    return
  }

  if (value === undefined) {
    return
  }

  if (value === null) {
    yield [key, '']

    return
  }

  if (typeof value === 'boolean') {
    yield [key, value ? '1' : '0']

    return
  }

  yield [key, value as string | Blob]
}

/**
 * Create headers for the request.
 * If the URL is on the same origin, the `X-XSRF-TOKEN` cookie will be added to the headers.
 */
function toHeaders(headers: Record<string, number | string | undefined>, url: string): Record<string, string> {
  const result: Record<string, string> = {}

  if (url.startsWith('/')) {
    headers['X-XSRF-TOKEN'] = getCookie('XSRF-TOKEN')
  } else {
    try {
      const parsedURL = new URL(url)
      if (parsedURL.protocol === window.location.protocol && parsedURL.host === window.location.host) {
        headers['X-XSRF-TOKEN'] = getCookie('XSRF-TOKEN')
      }
    } catch {
      // If the URL is invalid, we assume it's on the same origin.
      headers['X-XSRF-TOKEN'] = getCookie('XSRF-TOKEN')
    }
  }

  // Remove duplicate headers.
  // The last header will take precedence.
  const lowercasedHeadersDone = new Set<string>()
  for (const [key, value] of Object.entries(headers).reverse()) {
    const lowercasedKey = key.toLowerCase()
    if (lowercasedHeadersDone.has(lowercasedKey)) {
      continue
    }
    if (value !== undefined) {
      result[key] = String(value)
      lowercasedHeadersDone.add(lowercasedKey)
    }
  }

  return result
}

/**
 * Create a query string from the given params.
 */
function toURLSearchParams(params: Record<string, unknown>): URLSearchParams {
  const urlSearchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        // key[] is PHP specific syntax for arrays.
        // We might want to make this configurable in the future.
        urlSearchParams.append(`${key}[]`, String(item))
      }
    } else {
      urlSearchParams.append(key, String(value))
    }
  }

  return urlSearchParams
}

/**
 * Get a cookie by name.
 */
function getCookie(name: string): string | undefined {
  return document.cookie
    .split(';')
    .findLast((cookie) => cookie.startsWith(` ${name}`))
    ?.replace(`${name}=`, '')
}

export function createHTTPClient(options: CreateHTTPClientOptions = {}): HTTPClient {
  const abortControllers: Record<string, AbortController> = {}

  let baseURL = options.baseURL ?? ''
  if (baseURL && !baseURL.endsWith('/')) {
    baseURL += '/'
  }

  let interceptorId = 1
  const interceptors = {
    request: {} as Record<number, (config: RequiredRequestOptions) => MaybePromise<RequiredRequestOptions>>,
    response: {} as Record<number, (response: Response) => MaybePromise<Response>>,
    error: {} as Record<number, (error: RequestError) => MaybePromise<RequestError | never>>,
  }

  function useInterceptor(interceptors: Record<number, unknown>, handler: unknown) {
    const id = interceptorId++
    interceptors[id] = handler

    return id
  }

  return {
    baseURL,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },

    interceptors: {
      request: {
        use: (handler) => useInterceptor(interceptors.request, handler),
        eject: (id: number) => delete interceptors.request[id],
      },
      response: {
        use: (handler) => useInterceptor(interceptors.response, handler),
        eject: (id: number) => delete interceptors.response[id],
      },
      error: {
        use: (handler) => useInterceptor(interceptors.error, handler),
        eject: (id: number) => delete interceptors.error[id],
      },
    },

    get<TResponseData = any>(url: string, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'GET', url, ...options })
    },

    patch<TResponseData = any>(url: string, data: any = undefined, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'PATCH', url, ...options, data })
    },

    patchForm<TResponseData = any>(url: string, data: any = undefined, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'PATCH', url, ...options, data: toFormData(data) })
    },

    put<TResponseData = any>(url: string, data: any = undefined, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'PUT', url, ...options, data })
    },

    putForm<TResponseData = any>(url: string, data: any = undefined, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'PUT', url, ...options, data: toFormData(data) })
    },

    post<TResponseData = any>(url: string, data: any = undefined, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'POST', url, ...options, data })
    },

    postForm<TResponseData = any>(url: string, data: any = undefined, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'POST', url, ...options, data: toFormData(data) })
    },

    delete<TResponseData = any>(url: string, options: RequestOptions = {}): Promise<{ data: TResponseData }> {
      return this.request({ method: 'DELETE', url, ...options })
    },

    async request<TResponseData = any>(options: RequiredRequestOptions): Promise<{ data: TResponseData }> {
      for (const interceptor of Object.values(interceptors.request)) {
        options = await interceptor(options)
      }
      let url = options.url
      let method = options.method
      if (url.startsWith('/')) {
        url = url.slice(1)
      }
      url = `${this.baseURL}${url}`
      if (options.params) {
        url += `?${toURLSearchParams(options.params).toString()}`
      }

      let abortController: AbortController | undefined = undefined
      let fingerprint = ''
      if (options.key !== null) {
        if (options.key) {
          fingerprint = Array.isArray(options.key) ? options.key.join(':') : String(options.key ?? '')
        }
        if (!fingerprint) {
          fingerprint = `${method}:${url}`
          try {
            const stacktrace = new Error().stack?.split('\n').slice(1, 4).join('')
            if (stacktrace) {
              fingerprint = `${fingerprint}:${stacktrace}`
            }
          } catch {
            //
          }
        }
        abortControllers[fingerprint]?.abort(`Request aborted due to a new request with the same fingerprint: ${fingerprint}`)
        abortController = new AbortController()
        abortControllers[fingerprint] = abortController
      }

      const init = {
        method,
        headers: toHeaders(
          {
            ...this.headers,
            ...options.headers,
          },
          url,
        ) as Record<string, string>,
        body: toBody(options),
        signal: abortController?.signal,
      } satisfies RequestInit

      if (init.body instanceof FormData) {
        delete init.headers['Content-Type']
      }

      let response = await fetch(url, init)

      if (response.ok) {
        for (const interceptor of Object.values(interceptors.response)) {
          response = await interceptor(response)
        }

        const result = {
          data: '' as string | Blob | TResponseData,
        }
        const contentType = response.headers.get('Content-Type') ?? ''
        if (contentType.includes('application/json') || contentType.includes('application/geo+json')) {
          result.data = await response.json()
        } else if (contentType.includes('application/octet-stream') || contentType.includes('application/pdf')) {
          result.data = await response.blob()
        } else {
          result.data = await response.text()
        }
        delete abortControllers[fingerprint]

        return result as { data: TResponseData }
      }

      delete abortControllers[fingerprint]
      const error = await RequestError.fromResponse(response)
      for (const interceptor of Object.values(interceptors.error)) {
        await interceptor(error)
      }

      throw error
    },
  }
}
